import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { Page, PageHeader, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, billedHoursFromSeconds } from "@/lib/labels";
import { Clock, Trash2, Plus, Play, Square, Repeat, Lock, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);
const curMonth = () => new Date().toISOString().slice(0, 7);
const mondayOf = (dt) => { const x = new Date(dt); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x.toISOString().slice(0, 10); };

const GOAL_CLS = {
  emerald: { bar: "bg-emerald-400", text: "text-emerald-400", badge: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  amber: { bar: "bg-amber-400", text: "text-amber-300", badge: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  rose: { bar: "bg-rose-400", text: "text-rose-300", badge: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
};

function WeekGoal({ total, billable, cap }) {
  const norm = cap || 37.5;
  const tot = total || 0;
  const pct = norm ? Math.min(100, (tot / norm) * 100) : 0;
  const diff = Math.round((tot - norm) * 10) / 10;
  const color = tot >= norm ? "emerald" : tot >= norm * 0.8 ? "amber" : "rose";
  const cls = GOAL_CLS[color];
  return (
    <div className="mb-5 rounded-lg border border-border bg-secondary/30 p-4" data-testid="week-goal">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="label-caps">Ukesmål (normert uke)</span>
        <div className="flex items-center gap-3 text-sm">
          <span><span className={`font-display text-lg ${cls.text}`} data-testid="week-goal-total">{tot}</span> <span className="text-muted-foreground">av {norm} t</span></span>
          <span className={`text-xs px-2 py-0.5 rounded border ${cls.badge}`} data-testid="week-goal-diff">{diff >= 0 ? "+" : ""}{diff} t</span>
        </div>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full ${cls.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-muted-foreground mt-2" data-testid="week-goal-billable">
        Herav fakturerbart (timebaserte kunder): <span className="text-emerald-400">{billable ?? 0} t</span>
      </p>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <p className="label-caps">{label}</p>
      <p className={`font-display text-2xl mt-1 ${accent || ""}`}>{value}</p>
    </div>
  );
}

function WeekGrid({ customers, activities }) {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [nr, setNr] = useState({ customer_id: "", activity: "" });

  const load = useCallback(() => {
    api.get(`/timesheet?week_start=${weekStart}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  const shift = (n) => { const d = new Date(weekStart); d.setDate(d.getDate() + n * 7); setWeekStart(d.toISOString().slice(0, 10)); };

  const saveCell = async (row, dateStr, raw) => {
    const hours = raw === "" ? 0 : Number(raw);
    if (isNaN(hours) || hours < 0) { toast.error("Ugyldig timeantall"); load(); return; }
    try {
      await api.post("/timesheet/cell", { customer_id: row.customer_id || null, customer_name: row.customer_name, activity: row.activity || null, date: dateStr, hours, billable: row.billable });
      load();
    } catch (e) { toast.error(apiError(e)); load(); }
  };

  const addRow = () => {
    const cust = customers.find((c) => c.id === nr.customer_id);
    const key = `${nr.customer_id || ""}||${nr.activity || ""}`;
    if (data.rows.some((r) => r.key === key)) { toast.error("Raden finnes allerede"); setAdding(false); setNr({ customer_id: "", activity: "" }); return; }
    const cells = {}; data.days.forEach((d) => { cells[d] = { hours: 0, invoiced: false }; });
    setData({ ...data, rows: [...data.rows, { key, customer_id: nr.customer_id || null, customer_name: cust ? cust.legal_name : "Internt / uten kunde", activity: nr.activity || "", billable: true, cells, week_total: 0 }] });
    setNr({ customer_id: "", activity: "" }); setAdding(false);
  };

  const wd = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];
  const dayNum = (iso) => iso.slice(8, 10) + "." + iso.slice(5, 7);
  if (!data) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-display text-lg">Ukesrutenett</h3>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => shift(-1)} data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm text-muted-foreground w-36 text-center">{dayNum(data.days[0])} – {dayNum(data.days[6])}</span>
          <Button size="sm" variant="secondary" onClick={() => shift(1)} data-testid="week-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="week-today">Denne uken</Button>
        </div>
      </div>
      <WeekGoal total={data.week_total} billable={data.week_billable} cap={data.weekly_capacity} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="px-3 py-2 text-left label-caps min-w-[200px]">Kunde / Aktivitet</th>
              {data.days.map((d, i) => (
                <th key={d} className={`px-2 py-2 text-center label-caps ${i >= 5 ? "text-muted-foreground/60" : ""}`}>{wd[i]}<br /><span className="text-[10px] font-normal">{dayNum(d)}</span></th>
              ))}
              <th className="px-3 py-2 text-center label-caps">Sum</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Ingen rader ennå. Klikk «Legg til rad» for å begynne å føre.</td></tr>}
            {data.rows.map((row) => (
              <tr key={row.key} className="border-b border-border/50" data-testid={`week-row-${row.key}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{row.customer_name}</div>
                  <div className="flex items-center gap-2">
                    {row.activity && <span className="text-xs text-muted-foreground">{row.activity}</span>}
                    {row.billing_type === "fastpris" && <span className="text-[10px] px-1.5 py-0.5 rounded border border-border bg-secondary text-muted-foreground" data-testid={`row-fastpris-${row.key}`}>Fastpris</span>}
                  </div>
                </td>
                {data.days.map((d, i) => {
                  const cell = row.cells[d];
                  return (
                    <td key={d} className={`px-1 py-1 text-center ${i >= 5 ? "bg-secondary/20" : ""}`}>
                      {cell.invoiced ? (
                        <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">{cell.hours || ""}<Lock className="w-3 h-3" /></span>
                      ) : (
                        <input key={`${row.key}-${d}-${weekStart}`} type="number" step="0.25" min="0" defaultValue={cell.hours || ""}
                          onBlur={(e) => saveCell(row, d, e.target.value)}
                          data-testid={`cell-${row.key}-${d}`}
                          className="w-14 bg-transparent border border-transparent hover:border-border focus:border-white/60 focus:bg-secondary rounded text-center py-1 outline-none transition-colors" />
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-center font-medium">{row.week_total || 0}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border">
              <td className="px-3 py-2 label-caps">Dagsum</td>
              {data.days.map((d) => <td key={d} className="px-2 py-2 text-center text-muted-foreground">{data.day_totals[d] || 0}</td>)}
              <td className="px-3 py-2 text-center font-semibold" data-testid="week-total">{data.week_total || 0}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {adding ? (
        <div className="flex flex-wrap items-end gap-2 mt-4 p-3 rounded-lg border border-border bg-secondary/30">
          <div className="min-w-[200px]">
            <Label className="label-caps">Kunde</Label>
            <Select value={nr.customer_id} onValueChange={(v) => setNr({ ...nr, customer_id: v })}>
              <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="week-add-customer"><SelectValue placeholder="Velg kunde" /></SelectTrigger>
              <SelectContent className="bg-popover border-border max-h-72">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <Label className="label-caps">Aktivitet</Label>
            <Select value={nr.activity} onValueChange={(v) => setNr({ ...nr, activity: v })}>
              <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="week-add-activity"><SelectValue placeholder="Valgfritt" /></SelectTrigger>
              <SelectContent className="bg-popover border-border max-h-72">{activities.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={addRow} disabled={!nr.customer_id} className="bg-white text-black hover:bg-white/90" data-testid="week-add-confirm">Legg til</Button>
          <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setNr({ customer_id: "", activity: "" }); }}>Avbryt</Button>
        </div>
      ) : (
        <Button size="sm" variant="secondary" className="mt-4" onClick={() => setAdding(true)} data-testid="week-add-row"><Plus className="w-4 h-4 mr-1" /> Legg til rad</Button>
      )}
      <p className="text-xs text-muted-foreground mt-3">Skriv timer rett i cellene – de lagres automatisk når du forlater cellen. Fakturerte timer er låst. Timer på <b>fastpris</b>-kunder registreres, men inngår ikke i timebasert fakturagrunnlag.</p>
    </div>
  );
}

function QuickCustomerList({ customers, recent, onPick }) {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const recentList = recent.map((r) => customers.find((c) => c.id === r.customer_id)).filter(Boolean);
  const results = term
    ? customers.filter((c) => c.legal_name.toLowerCase().includes(term) || (c.org_number || "").includes(term)).slice(0, 30)
    : recentList;
  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-6" data-testid="quick-customer-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-display text-lg flex items-center gap-2"><Clock className="w-5 h-5 text-muted-foreground" /> Hurtigføring på kunde</h3>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk kunde…" className="pl-9 bg-secondary border-border" data-testid="quick-customer-search" />
        </div>
      </div>
      {!term && <p className="label-caps mb-2">Nylige kunder</p>}
      {results.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="quick-customer-empty">{term ? "Ingen treff." : "Ingen nylige kunder ennå – før tid, så dukker de opp her."}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {results.map((c) => (
            <button key={c.id} onClick={() => onPick(c)} data-testid={`quick-pick-${c.id}`}
              className="flex items-center justify-between gap-2 rounded-lg border border-border bg-secondary/40 hover:border-white/40 hover:bg-secondary transition-colors px-3 py-2.5 text-left group">
              <span className="text-sm font-medium truncate">{c.legal_name}</span>
              <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md bg-white text-black group-hover:scale-105 transition-transform"><Plus className="w-4 h-4" /></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TimeTab({ customers, month }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activities, setActivities] = useState([]);
  const [recent, setRecent] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ customer_id: "", activity: "", date: todayISO(), hours: "", description: "", billable: true });
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [view, setView] = useState("uke");
  const startRef = useRef(null);
  const formRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/time-entries?mine=true&month=${month}`).then((r) => setEntries(r.data)).catch(() => {});
    api.get(`/time-entries/summary?mine=true&month=${month}`).then((r) => setSummary(r.data)).catch(() => {});
    api.get("/time-entries/recent-customers?limit=20").then((r) => setRecent(r.data)).catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/activity-types").then((r) => setActivities(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(id);
  }, [running]);

  const startTimer = () => { startRef.current = Date.now(); setElapsed(0); setRunning(true); };
  const stopTimer = () => {
    setRunning(false);
    const h = billedHoursFromSeconds((Date.now() - startRef.current) / 1000);
    setF((p) => ({ ...p, hours: String(h) }));
    setElapsed(0);
  };
  const fmtElapsed = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const addHours = (h) => setF((p) => ({ ...p, hours: String(Number(p.hours || 0) + h) }));

  const pick = (c) => {
    setF((p) => ({ ...p, customer_id: c.id }));
    setView("skjema");
    toast.success(`${c.legal_name} valgt – fyll inn timer`);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const save = async () => {
    if (!f.hours || Number(f.hours) <= 0) { toast.error("Angi antall timer"); return; }
    setSaving(true);
    try {
      const cust = customers.find((c) => c.id === f.customer_id);
      await api.post("/time-entries", {
        customer_id: f.customer_id || null, customer_name: cust ? cust.legal_name : null,
        activity: f.activity || null, date: f.date, hours: Number(f.hours),
        description: f.description, billable: f.billable,
      });
      toast.success("Timeføring lagret");
      setF((p) => ({ ...p, hours: "", description: "" }));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const repeatLast = () => {
    if (!entries.length) { toast.error("Ingen tidligere føring å gjenta"); return; }
    const e = entries[0];
    setF({ customer_id: e.customer_id || "", activity: e.activity || "", date: todayISO(), hours: "", description: e.description || "", billable: e.billable });
    toast.success("Forrige føring gjenbrukt – fyll inn timer");
  };
  const del = async (id) => { try { await api.delete(`/time-entries/${id}`); toast.success("Slettet"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <>
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Stat label="Timer totalt" value={summary.total_hours} />
          <Stat label="Fakturerbart" value={summary.billable_hours} accent="text-emerald-400" />
          <Stat label="Ikke-fakturerbart" value={summary.non_billable_hours} />
          <Stat label="Fakturerbar andel" value={`${summary.billable_ratio}%`} />
        </div>
      )}

      <QuickCustomerList customers={customers} recent={recent} onPick={pick} />

      <div className="inline-flex rounded-lg border border-border bg-secondary/40 p-1 mb-6">
        <button type="button" onClick={() => setView("uke")} data-testid="view-uke" className={`text-sm px-3 py-1.5 rounded-md transition-colors ${view === "uke" ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}>Ukesrutenett</button>
        <button type="button" onClick={() => setView("skjema")} data-testid="view-skjema" className={`text-sm px-3 py-1.5 rounded-md transition-colors ${view === "skjema" ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}>Skjema</button>
      </div>
      {view === "uke" && <WeekGrid customers={customers} activities={activities} />}
      {view === "skjema" && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" ref={formRef}>
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg">Før timer</h3>
            <Button size="sm" variant="ghost" onClick={repeatLast} data-testid="time-repeat-btn"><Repeat className="w-4 h-4 mr-1" /> Gjenta forrige</Button>
          </div>
          <div className="flex items-center gap-3 mb-4 rounded-lg border border-border bg-secondary/40 p-3">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="font-mono text-lg tabular-nums" data-testid="time-stopwatch">{fmtElapsed(elapsed)}</span>
            {running
              ? <Button size="sm" variant="secondary" onClick={stopTimer} data-testid="time-stop-btn" className="ml-auto"><Square className="w-4 h-4 mr-1" /> Stopp</Button>
              : <Button size="sm" variant="secondary" onClick={startTimer} data-testid="time-start-btn" className="ml-auto"><Play className="w-4 h-4 mr-1" /> Start</Button>}
          </div>
          <div className="space-y-3">
            <div>
              <Label className="label-caps">Kunde</Label>
              <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
                <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="time-customer-select"><SelectValue placeholder="Velg kunde (valgfritt)" /></SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-72">
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Aktivitet</Label>
              <Select value={f.activity} onValueChange={(v) => setF({ ...f, activity: v })}>
                <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="time-activity-select"><SelectValue placeholder="Velg aktivitet (valgfritt)" /></SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-72">
                  {activities.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-caps">Dato</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="time-date" /></div>
              <div><Label className="label-caps">Timer</Label><Input type="number" step="0.25" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="time-hours" placeholder="0" /></div>
            </div>
            <div className="flex flex-wrap gap-2">
              {[0.25, 0.5, 1, 2].map((h) => <button key={h} type="button" onClick={() => addHours(h)} data-testid={`time-quick-${h}`} className="text-xs px-2.5 py-1 rounded-md border border-border bg-secondary hover:bg-secondary/70 transition-colors">+{h} t</button>)}
              <button type="button" onClick={() => setF({ ...f, hours: "" })} className="text-xs px-2.5 py-1 rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors">Nullstill</button>
            </div>
            <div><Label className="label-caps">Beskrivelse</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="time-desc" placeholder="Hva jobbet du med?" /></div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.billable} onCheckedChange={(v) => setF({ ...f, billable: v })} data-testid="time-billable" /> Fakturerbart</label>
            <Button onClick={save} disabled={saving} className="w-full bg-white text-black hover:bg-white/90" data-testid="time-save-btn"><Plus className="w-4 h-4 mr-1" /> {saving ? "Lagrer…" : "Lagre timeføring"}</Button>
          </div>
        </div>
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6">
          <h3 className="font-display text-lg mb-4">Mine timer · {month}</h3>
          {entries.length === 0 ? <Empty icon={Clock} title="Ingen timer ført" hint="Registrer timer i skjemaet til venstre." /> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">{["Dato", "Kunde", "Aktivitet", "Beskrivelse", "Timer", "Fakt.", ""].map((h) => <th key={h} className="px-3 py-2 label-caps">{h}</th>)}</tr></thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-border/50" data-testid={`time-row-${e.id}`}>
                    <td className="px-3 py-2 text-muted-foreground">{fmtDate(e.date)}</td>
                    <td className="px-3 py-2">{e.customer_name || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{e.activity || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{e.description || "—"}</td>
                    <td className="px-3 py-2 font-medium">{e.hours}</td>
                    <td className="px-3 py-2">{e.billable ? <span className="text-emerald-400">Ja</span> : <span className="text-muted-foreground">Nei</span>}</td>
                    <td className="px-3 py-2">{e.invoiced ? <Lock className="w-4 h-4 text-muted-foreground" /> : <button onClick={() => del(e.id)} className="text-muted-foreground hover:text-destructive" data-testid={`time-del-${e.id}`}><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}
    </>
  );
}

export default function Timeforing() {
  const [customers, setCustomers] = useState([]);
  const [month, setMonth] = useState(curMonth());

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  return (
    <Page>
      <PageHeader title="Timeføring" subtitle="Registrer timer – ukesrutenett, skjema eller hurtig på kunde" testid="timeforing-header">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 bg-secondary border-border" data-testid="timeforing-month" />
      </PageHeader>
      <TimeTab customers={customers} month={month} />
    </Page>
  );
}
